import { PRESSURE_VALUE_TYPE } from 'App/Map/CurrentValuesMap/_utils/by-domain/_utils/irrigationActivityConstants'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { doAnyPointsHavePressureValues } from './doAnyPointsHavePressureValues'

export const useIrrigationSelectData = ({ includePressure = false }: { includePressure?: boolean } = {}) => {
  // TODO: determine which properties are in view to dynamically build out the list of emitters
  const { fieldAssetDataMD5Hash, properties } = fieldAssetStore.useSelector((s) => ({
    /**
     * the fieldAssetDataMD5Hash is handy since it is very likely
     * to change when a user's field assets refresh and change
     */
    fieldAssetDataMD5Hash: s.fieldAssetDataMD5Hash,
    properties: s.properties,
  }))

  return useMemo(() => {
    let hasNorthernHemisphereProperties = false
    let hasSouthernHemisphereProperties = false

    Object.values(properties ?? {}).forEach((property) => {
      hasNorthernHemisphereProperties = hasNorthernHemisphereProperties || !property.isSouthernHemisphere

      hasSouthernHemisphereProperties = hasSouthernHemisphereProperties || property.isSouthernHemisphere
    })

    const returner = [
      {
        label: translate.phrases.banyanApp('Status'),
        value: 'irrigationIsOn_status',
      },
      ...(includePressure && doAnyPointsHavePressureValues(properties)
        ? [{ label: translate.phrases.banyanApp('Pressure'), value: PRESSURE_VALUE_TYPE }]
        : []),
      {
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Today'),
        }),
        value: 'irrigationActivity_todaySoFarSum_ms',
      },
      {
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Prev {{count}} Days', { count: 7 }),
        }),
        value: 'irrigationActivity_previous7DaysSum_ms',
      },
      {
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Prev {{count}} Days', { count: 30 }),
        }),
        value: 'irrigationActivity_previous30DaysSum_ms',
      },
    ]

    if (hasNorthernHemisphereProperties) {
      returner.push({
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Since {{date}}', {
            date: translate.dates.format(moment.tz('UTC').month(0).date(1), 'MMM D'), // Jan 1
          }),
        }),
        value: 'irrigationActivity_sinceJan1Sum_ms',
      })
    }

    if (hasSouthernHemisphereProperties) {
      returner.push({
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Since {{date}}', {
            date: translate.dates.format(moment.tz('UTC').month(3).date(1), 'MMM D'), // Apr 1
          }),
        }),
        value: 'irrigationActivity_sinceApr1Sum_ms',
      })
    }

    if (hasNorthernHemisphereProperties) {
      returner.push({
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Since {{date}}', {
            date: translate.dates.format(moment.tz('UTC').month(6).date(1), 'MMM D'), // Jul 1
          }),
        }),
        value: 'irrigationActivity_sinceJul1Sum_ms',
      })
    }

    if (hasSouthernHemisphereProperties) {
      returner.push({
        label: translate.phrases.templates('{{labelA}} ({{labelB}})', {
          labelA: translate.phrases.banyanApp('Applied'),
          labelB: translate.phrases.banyanApp('Since {{date}}', {
            date: translate.dates.format(moment.tz('UTC').month(9).date(1), 'MMM D'), // Oct 1
          }),
        }),
        value: 'irrigationActivity_sinceOct1Sum_ms',
      })
    }

    return returner
  }, [fieldAssetDataMD5Hash, includePressure])
}
